/**
 * Global date formatting and handling utilities for Cleanifax CRM
 * Fixes timezone issues and provides consistent date display
 */

/**
 * Format a date as "MMM d, yyyy" (e.g., "Oct 28, 2025")
 * @param {Date|string} date - Date object or date string
 * @returns {string} Formatted date string
 */
export const formatDate = (date) => {
  if (!date) return '';
  
  const d = typeof date === 'string' ? new Date(date) : date;
  
  if (isNaN(d.getTime())) return '';
  
  return new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(d);
};

/**
 * Format a datetime as "MMM d, yyyy • h:mm a" (e.g., "Oct 28, 2025 • 2:30 PM")
 * @param {Date|string} datetime - Date object or datetime string
 * @returns {string} Formatted datetime string
 */
export const formatDateTime = (datetime) => {
  if (!datetime) return '';
  
  const d = typeof datetime === 'string' ? new Date(datetime) : datetime;
  
  if (isNaN(d.getTime())) return '';
  
  const datePart = new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(d);
  
  const timePart = new Intl.DateTimeFormat('en-CA', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(d);
  
  return `${datePart} • ${timePart}`;
};

/**
 * Convert a Date to YYYY-MM-DD string (date-only, no timezone conversion)
 * Used for storing date-only fields in database
 * @param {Date} date - Date object
 * @returns {string} YYYY-MM-DD string
 */
export const toDateOnlyString = (date) => {
  if (!date) return '';
  
  const d = date instanceof Date ? date : new Date(date);
  
  if (isNaN(d.getTime())) return '';
  
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  
  return `${year}-${month}-${day}`;
};

/**
 * Parse YYYY-MM-DD string to Date at local noon (avoids timezone drift)
 * Used for reading date-only fields from database into date pickers
 * @param {string} dateString - YYYY-MM-DD string
 * @returns {Date} Date object at 12:00 local time
 */
export const fromDateOnlyString = (dateString) => {
  if (!dateString) return null;
  
  const parts = dateString.split('-');
  if (parts.length !== 3) return null;
  
  const year = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10) - 1; // JavaScript months are 0-indexed
  const day = parseInt(parts[2], 10);
  
  // Create date at noon local time to avoid timezone issues
  return new Date(year, month, day, 12, 0, 0);
};

/**
 * Add days to a date (for calculating due dates)
 * @param {Date|string} date - Starting date
 * @param {number} days - Number of days to add
 * @returns {Date} New date with days added
 */
export const addDays = (date, days) => {
  const d = typeof date === 'string' ? fromDateOnlyString(date) : new Date(date);
  const result = new Date(d);
  result.setDate(result.getDate() + days);
  return result;
};

/**
 * Calculate due date based on issue date and terms
 * @param {string} issueDate - Issue date in YYYY-MM-DD format
 * @param {string} terms - 'net_7', 'net_15', 'net_30', or 'upon_receipt'
 * @returns {string|null} Due date in YYYY-MM-DD format, or null for upon_receipt
 */
export const calculateDueDate = (issueDate, terms) => {
  if (!issueDate || terms === 'upon_receipt') return null;
  
  const daysMap = {
    'net_7': 7,
    'net_15': 15,
    'net_30': 30,
  };
  
  const days = daysMap[terms];
  if (!days) return null;
  
  const dueDate = addDays(issueDate, days);
  return toDateOnlyString(dueDate);
};

/**
 * Format relative time (e.g., "2 hours ago", "in 3 days")
 * @param {Date|string} date - Date to compare
 * @returns {string} Relative time string
 */
export const formatRelativeTime = (date) => {
  if (!date) return '';
  
  const d = typeof date === 'string' ? new Date(date) : date;
  
  if (isNaN(d.getTime())) return '';
  
  const now = new Date();
  const diffMs = d - now;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);
  
  if (Math.abs(diffSec) < 60) return 'just now';
  if (Math.abs(diffMin) < 60) return diffMin > 0 ? `in ${diffMin} minutes` : `${Math.abs(diffMin)} minutes ago`;
  if (Math.abs(diffHour) < 24) return diffHour > 0 ? `in ${diffHour} hours` : `${Math.abs(diffHour)} hours ago`;
  if (Math.abs(diffDay) < 7) return diffDay > 0 ? `in ${diffDay} days` : `${Math.abs(diffDay)} days ago`;
  
  return formatDate(d);
};

/**
 * Check if a date is today
 * @param {Date|string} date - Date to check
 * @returns {boolean} True if date is today
 */
export const isToday = (date) => {
  if (!date) return false;
  
  const d = typeof date === 'string' ? new Date(date) : date;
  const today = new Date();
  
  return d.getDate() === today.getDate() &&
         d.getMonth() === today.getMonth() &&
         d.getFullYear() === today.getFullYear();
};

/**
 * Get today's date as YYYY-MM-DD string
 * @returns {string} Today's date in YYYY-MM-DD format
 */
export const getTodayString = () => {
  return toDateOnlyString(new Date());
};
